import { utc } from '@date-fns/utc';
import { eachYearOfInterval, endOfYear, fromUnixTime, getMonth, getUnixTime, getYear, startOfYear } from 'date-fns';
import { secondsInDay } from 'date-fns/constants';
import { flow, makeObservable, observable, reaction } from 'mobx';
import { computedFn } from 'mobx-utils';

import type { TDictObject } from 'src/api/directories/types';
import { calculateCommercialSpeed } from 'src/features/well-form/utils';
import { hasValue } from 'src/shared/utils/common';
import type { Directories } from 'src/store/directories/directories.store';
import type { PlanVersionsService } from 'src/store/plan-version/plan-versions-service';

import { TheWellEntity } from './entities';
import type { TRawPassing, TRawRigOperationSection, TRawRigOperationStage, TTheWell } from './types';
import { WellsServiceApi } from './wells-service.api';

export class WellsService {
  private readonly api: WellsServiceApi;
  private readonly planVersionService: PlanVersionsService;
  private readonly directories: Directories;

  theWellsMap: Map<string, TheWellEntity> = new Map();
  @observable.ref theWells: TheWellEntity[] = [];
  @observable isWellsFetching: boolean = false;
  @observable isWellsUpdating: boolean = false;
  @observable isInitializedData: boolean = false;

  @observable.ref blocks: TDictObject[] = [];
  @observable.ref fields: TDictObject[] = [];
  @observable.ref organizations: TDictObject[] = [];
  @observable.ref pads: TDictObject[] = [];
  @observable.ref fluidTypes: TDictObject[] = [];
  @observable.ref wellTypes: TDictObject[] = [];
  @observable.ref coreSamplingIntervals: TDictObject[] = [];
  @observable.ref clusters: TDictObject[] = [];
  @observable.ref reservoirs: TDictObject[] = [];
  @observable.ref drillingStatuses: TDictObject[] = [];
  @observable.ref blockFieldLinks: TDictObject[] = [];
  @observable.ref wellPurposes: TDictObject[] = [];
  @observable.ref wellEndingTypes: TDictObject[] = [];
  @observable.ref wellCategories: TDictObject[] = [];
  @observable.ref rigs: TDictObject[] = [];
  @observable.ref brigades: TDictObject[] = [];
  @observable.ref padTypes: TDictObject[] = [];
  @observable.ref passageTypes: TDictObject[] = [];
  @observable.ref rigModels: TDictObject[] = [];
  @observable.ref sectionTypes: TDictObject[] = [];
  @observable.ref wellLifeCycleStages: TDictObject[] = [];

  constructor(directories: Directories, planVersionService: PlanVersionsService) {
    this.api = new WellsServiceApi();
    this.planVersionService = planVersionService;
    this.directories = directories;

    makeObservable(this);
  }

  @flow.bound
  private async *loadDirectories(): Promise<void> {
    const [
      blocks,
      fields,
      organizations,
      pads,
      fluidTypes,
      wellTypes,
      coreSamplingIntervals,
      clusters,
      reservoirs,
      drillingStatuses,
      blockFieldLinks,
      wellPurposes,
      wellEndingTypes,
      wellCategories,
      rigs,
      brigades,
      padTypes,
      passageTypes,
      rigModels,
      sectionTypes,
      wellLifeCycleStages,
      _organizationActivities,
    ] = await this.directories.loadObjects([
      'Common_Block',
      'Common_Field',
      'Common_Organization',
      'Common_Pad',
      'Common_FluidType',
      'Common_WellType',
      'Common_CoreSamplingInterval',
      'Common_Cluster',
      'Common_Reservoir',
      'Common_DrillingStatus',
      'Common_BlockFieldLink',
      'Common_WellPurpose',
      'Common_WellEndingType',
      'Common_WellCategory',
      'Common_Rig',
      'Common_Brigade',
      'Common_PadType',
      'Common_PassageType',
      'Common_Rig_Model',
      'Common_SectionType',
      'GOplan_Dict_WellLifeCycleStage',
      'Common_OrganizationActivity',
    ]);
    yield;

    this.blocks = blocks ?? [];
    this.fields = fields ?? [];
    this.organizations = organizations ?? [];
    this.pads = pads ?? [];
    this.fluidTypes = fluidTypes ?? [];
    this.wellTypes = wellTypes ?? [];
    this.coreSamplingIntervals = coreSamplingIntervals ?? [];
    this.clusters = clusters ?? [];
    this.reservoirs = reservoirs ?? [];
    this.drillingStatuses = drillingStatuses ?? [];
    this.blockFieldLinks = blockFieldLinks ?? [];
    this.wellPurposes = wellPurposes ?? [];
    this.wellEndingTypes = wellEndingTypes ?? [];
    this.wellCategories = wellCategories ?? [];
    this.rigs = rigs ?? [];
    this.brigades = brigades ?? [];
    this.padTypes = padTypes ?? [];
    this.passageTypes = passageTypes ?? [];
    this.rigModels = rigModels ?? [];
    this.sectionTypes = sectionTypes ?? [];
    this.wellLifeCycleStages = wellLifeCycleStages ?? [];
  }

  private mapPassingByMonth(passing?: TRawPassing[]): Record<number, Record<number | 'total', number>> {
    if (!passing || !passing.length) {
      return {};
    }

    return passing.reduce((acc: Record<number, Record<number | 'total', number>>, curr) => {
      if (!curr.date || !curr.passing) {
        return acc;
      }

      const year = getYear(fromUnixTime(curr.date, { in: utc }), { in: utc });
      const month = getMonth(fromUnixTime(curr.date, { in: utc }), { in: utc });

      if (!acc[year]) {
        acc[year] = { total: 0 };
      }

      if (!acc[year][month]) {
        acc[year][month] = 0;
      }

      acc[year].total += curr.passing;
      acc[year][month] += curr.passing;

      return acc;
    }, {});
  }

  private mapStagesByStageType(stages?: TRawRigOperationStage[]): Record<number, TRawRigOperationStage> {
    if (!stages || !stages.length) {
      return {};
    }

    return stages.reduce((acc: Record<number, TRawRigOperationStage>, curr) => {
      const wellLifeCycleStageId = curr.wellLifeCycleStageId;

      if (!wellLifeCycleStageId) {
        return acc;
      }

      acc[wellLifeCycleStageId] = curr;

      return acc;
    }, {});
  }

  private mapSectionsBySectionType(
    stages: Record<number, TRawRigOperationStage>,
  ): Record<number, TRawRigOperationSection> {
    const sectionsMap: Record<number, TRawRigOperationSection> = {};

    for (const stage of Object.values(stages)) {
      const sections = stage.sections;

      if (!sections || !sections.length) {
        continue;
      }

      for (const section of sections) {
        const sectionTypeId = section.sectionTypeId;

        if (!sectionTypeId) {
          continue;
        }

        sectionsMap[sectionTypeId] = section;
      }
    }

    return sectionsMap;
  }

  private isDrillingStage = computedFn((stageTypeId: number | null): boolean => {
    if (!hasValue(stageTypeId)) {
      return false;
    }

    const directory = this.directories.getObject('GOplan_Dict_WellLifeCycleStage');

    if (!directory) {
      console.error('GOplan_Dict_WellLifeCycleStage directory is not presented');
      return false;
    }

    return directory.find((dv) => dv.id === stageTypeId)?.data['slug'] === 'drilling';
  });

  private mapCsByYears(
    passingByMonth: Record<number, Record<number | 'total', number>>,
    stages?: TRawRigOperationStage[],
  ): Record<number, number> {
    if (!stages || !stages.length) {
      return {};
    }

    const durationByYears = stages.reduce((acc: Record<number, number>, curr) => {
      if (!this.isDrillingStage(curr.wellLifeCycleStageId)) {
        return acc;
      }

      const startDate = curr.startDate;
      const endDate = curr.endDate;

      if (!startDate || !endDate) {
        return acc;
      }

      const startYear = startOfYear(fromUnixTime(startDate, { in: utc }), { in: utc });
      const endYear = endOfYear(fromUnixTime(endDate, { in: utc }), { in: utc });

      const duration = Math.min(endDate, getUnixTime(endYear)) - Math.max(startDate, getUnixTime(startYear));

      if (startYear === endYear) {
        if (!acc[getYear(startYear, { in: utc })]) {
          acc[getYear(startYear, { in: utc })] = 0;
        }

        acc[getYear(startYear, { in: utc })] += duration;
        return acc;
      }

      const years = eachYearOfInterval({ start: startYear, end: endYear });

      for (const year of years) {
        const yearStart = getUnixTime(startOfYear(year, { in: utc }));
        const yearEnd = getUnixTime(endOfYear(year, { in: utc }));

        const yearDuration = Math.min(endDate, yearEnd) - Math.max(startDate, yearStart);

        if (!acc[getYear(year, { in: utc })]) {
          acc[getYear(year, { in: utc })] = 0;
        }

        acc[getYear(year, { in: utc })] += yearDuration;
      }

      return acc;
    }, {});

    const csByYears = Object.entries(durationByYears).reduce((acc: Record<number, number>, [year, duration]) => {
      const passing = passingByMonth[Number(year)];

      if (!passing?.total) {
        console.warn(
          'cannot find passing for year',
          year,
          'passing:',
          passing,
          'passingByMonth:',
          passingByMonth,
          'stages:',
          stages,
        );
        return acc;
      }

      const days = duration / secondsInDay;

      acc[Number(year)] = calculateCommercialSpeed(passing.total, days);

      return acc;
    }, {});

    return csByYears;
  }

  private mapWells(wells: TTheWell[]): { mappedWells: TheWellEntity[]; theWellsMap: Map<string, TheWellEntity> } {
    const theWellsMap = new Map<string, TheWellEntity>();

    const mappedWells = wells.map((well) => {
      const passingByMonth = this.mapPassingByMonth(well.passing);
      const csByYears = this.mapCsByYears(passingByMonth, well.stages);
      const stagesByStageType = this.mapStagesByStageType(well.stages);
      const sectionsBySectionType = this.mapSectionsBySectionType(stagesByStageType);

      const data = {
        ...well,
        passingByMonth,
        stagesByStageType,
        sectionsBySectionType,
        csByYears,
      };

      const theWell = new TheWellEntity(data);
      theWellsMap.set(theWell.entityId, theWell);

      return theWell;
    });

    return {
      mappedWells,
      theWellsMap: theWellsMap,
    };
  }

  @flow.bound
  async *fetchWells(planVersionId: number) {
    this.isWellsFetching = true;

    try {
      const wells = await this.api.fetchWells(planVersionId);
      yield;

      const { mappedWells, theWellsMap } = this.mapWells(wells);

      this.theWells = mappedWells;
      this.theWellsMap = theWellsMap;

      this.isInitializedData = true;
    } catch (e) {
      console.error(e);
    } finally {
      this.isWellsFetching = false;
    }
  }

  @flow.bound
  async *deleteWells(wellIds: string[]): Promise<boolean> {
    if (!this.planVersionService.currentDraftId) {
      console.error('planVersionId is not presented');
      return false;
    }

    this.isWellsUpdating = true;
    const wellIdsNumbers = wellIds
      .map((id) => {
        const well = this.theWellsMap.get(id);

        if (!well) {
          console.error('well is not presented');
          return null;
        }

        return well.data.geologicalTaskId;
      })
      .filter(hasValue);

    try {
      await this.api.deleteWells(this.planVersionService.currentDraftId, wellIdsNumbers);
      yield;

      this.theWells = this.theWells.filter((well) => !wellIdsNumbers.includes(well.data.geologicalTaskId));
      wellIds.forEach((id) => this.theWellsMap.delete(id));

      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      this.isWellsUpdating = false;
    }
  }

  init(): VoidFunction {
    this.loadDirectories();

    const disposer = reaction(
      () => this.planVersionService.currentDraftId ?? this.planVersionService.currentPlanVersion?.id ?? null,
      (planVersionId) => {
        if (hasValue(planVersionId)) {
          this.fetchWells(planVersionId);
        }
      },
      { fireImmediately: true },
    );

    return () => {
      disposer();
    };
  }
}
